/**
 * @module data/services
 *
 * The consulting offer. Deliberately short: three ways to engage, each with a
 * clear outcome, a typical shape and the honest prerequisite for it to work.
 */

import { COMPANY } from "./site.js";

/**
 * @typedef {Object} Service
 * @property {string}   id
 * @property {string}   name
 * @property {string}   tagline
 * @property {string}   body
 * @property {string}   outcome
 * @property {string}   shape       Typical engagement shape and duration.
 * @property {string[]} includes
 * @property {string}   prerequisite  What must be true for this to work.
 * @property {string}   glyph
 * @property {string}   accent
 * @property {boolean}  [primary]
 */

/** @type {Service[]} */
export const SERVICES = [
  {
    id: "advisory",
    name: "Architecture & AI advisory",
    tagline: "A method, a mandate and a map.",
    body: "We stand up the architecture capability and run the first full ADM cycle with you: principles, governance, vision, and a target architecture with a funded roadmap behind it.",
    outcome: "An architecture practice you own and a roadmap your board has approved.",
    shape: "6–12 weeks, one senior architect plus a specialist, working with your team.",
    includes: [
      "Architecture principles and tailored ADM",
      "Governance model and decision rights",
      "Capability model, value streams and heat map",
      "Target architecture and transition roadmap",
      "AI opportunity and feasibility assessment",
    ],
    prerequisite: "An executive sponsor who can make decisions in the room.",
    glyph: "compass",
    accent: "#d8a657",
    primary: true,
  },
  {
    id: "delivery",
    name: "Embedded transformation delivery",
    tagline: "We stay for the difficult part.",
    body: "We embed alongside your teams through implementation governance: compliance reviews, AI assurance, migration orchestration and value tracking until the capability is genuinely in use.",
    outcome: "Delivered increments that pass architecture review and adoption measures.",
    shape: "6–24 months, a blended team with your architects and delivery leads.",
    includes: [
      "Work package definition and sequencing",
      "Architecture compliance and dispensation management",
      "AI evaluation, guardrails and release assurance",
      "Change and adoption workstream leadership",
      "Benefit realisation and value reconciliation",
    ],
    prerequisite: "Commitment to architecture governance, including the right to say no.",
    glyph: "rocket",
    accent: "#5fd4c4",
  },
  {
    id: "enablement",
    name: "Capability enablement",
    tagline: "So that you do not need us again.",
    body: "We transfer the method: pairing on live decisions, certifying your architects, and leaving behind the standards, templates and repository that keep the practice running.",
    outcome: "An internal architecture and AI practice that runs the method unaided.",
    shape: "3–9 months, cohort-based, delivered against real decisions rather than case studies.",
    includes: [
      "Role-based curriculum for architects and product leads",
      "Pairing on live architecture decisions",
      "Certification against the tailored ADM",
      "Repository, templates and standards handover",
      "Responsible AI and evaluation training",
    ],
    prerequisite: "Named people with protected time to learn.",
    glyph: "book",
    accent: "#a98bc4",
  },
];

/** Section copy. */
export const SERVICES_SUMMARY = {
  eyebrow: "Consulting services",
  title: "Three ways to work with us",
  lede:
    "We are deliberately narrow. We do architecture, we do the delivery governance that makes architecture real, and we leave capability behind. Everything else, we will recommend someone better for.",
};

/** The call-to-action block at the end of the page. */
export const CTA = {
  eyebrow: "Start here",
  title: "Begin with a method conversation",
  lede:
    "Ninety minutes, no slideware. Bring the change you are trying to make and the decision you cannot get unstuck. We will tell you which phase you are actually in — and whether we are the right people for it.",
  primaryCta: {
    label: "Book a method conversation",
    // The declared contact address, so the site never advertises a mailbox that
    // the Impressum does not list.
    href: `mailto:${COMPANY.email}?subject=Method%20conversation`,
  },
  secondaryCta: { label: "Copy the ADM checklist", href: "#adm" },
  note: "We reply within two working days. If we are not the right fit, we will say so and suggest who is.",
};
